import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { PictureAsPdf as PictureAsPdfIcon, Image as ImageIcon, InsertDriveFile as InsertDriveFileIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AppStyles from '../styles/AppStyles';

const FileViewer = ({ 
  open, 
  onClose, 
  files = [], 
  currentIndex = 0, 
  onIndexChange 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [currentFile, setCurrentFile] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [blobUrl, setBlobUrl] = useState(null);

  // Cleanup blob URLs when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    };
  }, [blobUrl]);

  const extractFileKeyFromUrl = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('key');
    } catch (error) {
      console.log('Error extracting file key from URL:', error);
      return null;
    }
  };

  const extractPublicUrl = useCallback((url) => {
    if (!url) return null;
    try {
      // If it's already a public URL, return it
      if (url.includes('r2.dev') || url.includes('your-public-domain.com')) {
        return url;
      }
      
      // Extract the file key and construct public URL
      const fileKey = extractFileKeyFromUrl(url);
      if (fileKey) {
        const publicBase = process.env.REACT_APP_R2_PUBLIC_BASE_URL || 'https://your-public-domain.com';
        return `${publicBase}/${fileKey}`;
      }
    } catch (error) {
      console.log('Error extracting public URL:', error);
    }
    return null;
  }, []);

  const loadFileContent = useCallback(async (file) => {
    if (!file?.url) {
      console.log('FileViewer: No URL provided for file:', file);
      return;
    }
    
    setLoading(true);
    setError(null);
    setFileContent(null);
    
    try {
      const fileType = inferFileType(file);
      console.log('FileViewer: Loading file:', {
        fileName: file.fileName,
        url: file.url,
        fileType: fileType
      });
      
      if (fileType === 'image' || (fileType && fileType.startsWith('image/'))) {
        // For images, we can display them directly
        setFileContent({
          type: 'image',
          url: file.url,
          fileName: file.fileName
        });
      } else if (fileType === 'application/pdf' || (fileType && fileType.includes('pdf'))) {
        // For PDFs, we'll use an iframe
        setFileContent({
          type: 'pdf',
          url: file.url,
          fileName: file.fileName
        });
      } else if (isConvertibleDocument(fileType, file.fileName)) {
        // Check if file is supported by Microsoft Office Viewer
        if (isOfficeViewerSupported(fileType, file.fileName)) {
          // Use Microsoft Office Viewer for Office documents
          console.log('FileViewer: Using Microsoft Office Viewer for:', file.fileName, 'URL:', file.url);
          
          // Extract the public URL from the file URL
          const publicUrl = extractPublicUrl(file.url);
          
          if (publicUrl) {
            // Use Microsoft Office Viewer API
            const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(publicUrl)}`;
            
            setFileContent({
              type: 'office-viewer',
              url: officeViewerUrl,
              fileName: file.fileName,
              originalFileType: fileType,
              publicUrl: publicUrl
            });
          } else {
            // Fallback to preview not available if no public URL available
            setFileContent({
              type: 'preview-not-available',
              url: file.url,
              fileName: file.fileName,
              fileType: fileType
            });
          }
        } else {
          // For text files (TXT, CSV), use direct display
          console.log('FileViewer: Using direct text display for:', file.fileName, 'URL:', file.url);
          
          // Extract the public URL from the file URL
          const publicUrl = extractPublicUrl(file.url);
          console.log('FileViewer: Extracted public URL:', publicUrl);
          
          if (publicUrl) {
            setFileContent({
              type: 'text-viewer',
              url: publicUrl,
              fileName: file.fileName,
              originalFileType: fileType
            });
          } else {
            // Fallback to preview not available if no public URL available
            console.log('FileViewer: No public URL available, showing preview not available');
            setFileContent({
              type: 'preview-not-available',
              url: file.url,
              fileName: file.fileName,
              fileType: fileType
            });
          }
        }
      } else {
        // For unsupported files, show preview not available message
        setFileContent({
          type: 'preview-not-available',
          url: file.url,
          fileName: file.fileName,
          fileType: fileType
        });
      }
    } catch (err) {
      console.error('Error loading file:', err);
      setError(t('fileViewer.failedToLoadFile'));
    } finally {
      setLoading(false);
    }
  }, [extractPublicUrl, t]); // Include extractPublicUrl and t in dependencies

  useEffect(() => {
    if (open && files.length > 0) {
      const file = files[currentIndex];
      setCurrentFile(file);
      loadFileContent(file);
    }
  }, [open, files, currentIndex, loadFileContent]);

  const inferFileType = (file) => {
    const fileName = file.fileName || file.url || '';
    const lower = fileName.toLowerCase();
    
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.doc')) return 'application/msword';
    if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (lower.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (lower.endsWith('.ppt')) return 'application/vnd.ms-powerpoint';
    if (lower.endsWith('.pptx')) return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    if (lower.endsWith('.pages')) return 'application/vnd.apple.pages';
    if (lower.endsWith('.txt')) return 'text/plain';
    if (lower.endsWith('.rtf')) return 'application/rtf';
    
    return file.fileType || 'application/octet-stream';
  };

  const isConvertibleDocument = (fileType, fileName) => {
    // Only include files that can actually be converted/previewed
    const convertibleTypes = [
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-powerpoint', // .ppt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/rtf', // .rtf
      'text/plain' // .txt
    ];
    
    // Check by MIME type first
    if (convertibleTypes.includes(fileType)) {
      return true;
    }
    
    // Also check by file extension as fallback
    if (fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      const convertibleExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'rtf', 'txt'];
      return convertibleExtensions.includes(extension);
    }
    
    return false;
  };

  const isOfficeViewerSupported = (fileType, fileName) => {
    // Microsoft Office Viewer only supports Microsoft Office documents, not Apple iWork or plain text
    const officeTypes = [
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-powerpoint', // .ppt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/rtf' // .rtf
    ];
    
    // Check by MIME type first
    if (officeTypes.includes(fileType)) {
      return true;
    }
    
    // Also check by file extension as fallback
    if (fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'rtf'];
      return officeExtensions.includes(extension);
    }
    
    return false;
  };


  const handleDownload = () => {
    if (currentFile?.url) {
      const link = document.createElement('a');
      link.href = currentFile.url;
      link.download = currentFile.fileName || t('common.file');
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < files.length - 1) {
      onIndexChange(currentIndex + 1);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
    // Reset drag offset when zooming out to fit
    if (zoom <= 1.25) {
      setDragOffset({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      const newOffset = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      };
      setDragOffset(newOffset);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };


  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf' || fileType.includes('pdf')) {
      return <PictureAsPdfIcon sx={{ fontSize: 40, color: AppStyles.colors.action }} />;
    }
    if (fileType.startsWith('image/')) {
      return <ImageIcon sx={{ fontSize: 40, color: AppStyles.colors.action }} />;
    }
    return <InsertDriveFileIcon sx={{ fontSize: 40, color: AppStyles.colors.action }} />;
  };

  const renderFileContent = () => {
    if (loading) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          gap: 2
        }}>
          <CircularProgress size={48} sx={{ color: AppStyles.colors.primary.main }} />
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t('fileViewer.loadingFile')}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          gap: 2,
          p: 4
        }}>
          <Alert severity="error" sx={{ borderRadius: 2, width: '100%', maxWidth: 400 }}>
            {error}
          </Alert>
        </Box>
      );
    }

    if (!fileContent) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          gap: 2
        }}>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t('fileViewer.noFileContent')}
          </Typography>
        </Box>
      );
    }

    switch (fileContent.type) {
      case 'image':
        return (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%', 
            overflow: 'hidden',
            backgroundColor: '#fff',
            position: 'relative',
            p: 2,
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          >
            <img
              src={fileContent.url}
              alt={fileContent.fileName}
              style={{
                maxWidth: 'calc(100% - 32px)',
                maxHeight: 'calc(100% - 32px)',
                width: 'auto',
                height: 'auto',
                transform: `scale(${zoom}) translate(${dragOffset.x / zoom}px, ${dragOffset.y / zoom}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                userSelect: 'none',
                pointerEvents: zoom > 1 ? 'auto' : 'none'
              }}
              onMouseDown={handleMouseDown}
              onError={() => setError(t('fileViewer.failedToLoadImage'))}
            />
          </Box>
        );

      case 'pdf':
        return (
          <Box sx={{ 
            height: '100%', 
            width: '100%',
            backgroundColor: '#fff',
            borderRadius: '8px 8px 0 0',
            overflow: 'hidden'
          }}>
            <iframe
              src={fileContent.url}
              width="100%"
              height="100%"
              style={{ 
                border: 'none',
                borderRadius: '8px 8px 0 0'
              }}
              title={fileContent.fileName}
              onError={() => setError(t('fileViewer.failedToLoadPdf'))}
            />
          </Box>
        );

      case 'office-viewer':
        return (
          <Box sx={{ 
            height: '100%', 
            width: '100%',
            backgroundColor: '#fff',
            borderRadius: '8px 8px 0 0',
            overflow: 'hidden'
          }}>
            <iframe
              src={fileContent.url}
              width="100%"
              height="100%"
              style={{ 
                border: 'none',
                borderRadius: '8px 8px 0 0'
              }}
              title={fileContent.fileName}
              onError={() => setError(t('fileViewer.failedToLoadDocument'))}
            />
          </Box>
        );

      case 'text-viewer':
        return (
          <Box sx={{ 
            height: '100%', 
            width: '100%',
            backgroundColor: '#fff',
            borderRadius: '8px 8px 0 0',
            overflow: 'hidden'
          }}>
            <iframe
              src={fileContent.url}
              width="100%"
              height="100%"
              style={{ 
                border: 'none',
                borderRadius: '8px 8px 0 0'
              }}
              title={fileContent.fileName}
              onError={() => setError(t('fileViewer.failedToLoadTextFile'))}
            />
          </Box>
        );

      case 'multi-page-images':
        return (
          <Box sx={{ 
            height: '100%', 
            width: '100%',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px 8px 0 0',
            overflow: 'auto',
            p: 2
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              alignItems: 'center'
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                mb: 1
              }}>
                {fileContent.fileName} ({fileContent.pageCount} {t('fileViewer.pages')})
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'center',
                maxWidth: '100%'
              }}>
                {fileContent.pages.map((page, index) => (
                  <Box key={index} sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="caption" sx={{ 
                      fontWeight: 500,
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}>
                      {t('fileViewer.page')} {page.pageNumber}
                    </Typography>
                    <img
                      src={`data:image/png;base64,${page.imageData}`}
                      alt={`Page ${page.pageNumber}`}
                      style={{
                        maxWidth: '300px',
                        maxHeight: '400px',
                        width: 'auto',
                        height: 'auto',
                        borderRadius: '4px',
                        objectFit: 'contain',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        // Open image in new tab for full view
                        const newWindow = window.open();
                        newWindow.document.write(`
                          <html>
                            <head><title>Page ${page.pageNumber} - ${fileContent.fileName}</title></head>
                            <body style="margin:0;padding:20px;background:#f5f5f5;text-align:center;">
                              <img src="data:image/png;base64,${page.imageData}" style="max-width:100%;max-height:100%;border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,0.2);" />
                            </body>
                          </html>
                        `);
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'preview-not-available':
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%', 
            gap: 3,
            backgroundColor: '#fff',
            borderRadius: 2,
            mx: 2,
            my: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              border: '2px solid #e0e0e0'
            }}>
              <Typography variant="h4" sx={{ color: '#999' }}>📄</Typography>
            </Box>
            <Typography variant="h5" color="text.primary" sx={{ fontWeight: 600, textAlign: 'center' }}>
              {t('fileViewer.previewNotAvailable')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ 
                mt: 1,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}, ${AppStyles.colors.secondary.main})`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                }
              }}
            >
              {t('fileViewer.downloadFile')}
            </Button>
          </Box>
        );

      case 'download':
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%', 
            gap: 3,
            backgroundColor: '#fff',
            borderRadius: 2,
            mx: 2,
            my: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: AppStyles.colors.primary.main + '15',
              border: `2px solid ${AppStyles.colors.primary.main}30`
            }}>
              {getFileIcon(fileContent.fileType)}
            </Box>
            <Typography variant="h5" color="text.primary" sx={{ fontWeight: 600, textAlign: 'center' }}>
              {fileContent.fileName}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ 
                mt: 1,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}, ${AppStyles.colors.secondary.main})`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                }
              }}
            >
              {t('fileViewer.downloadFile')}
            </Button>
          </Box>
        );

      default:
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            gap: 2
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('fileViewer.unsupportedFileType')}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: { xs: '98vh', md: '95vh' },
          maxHeight: { xs: '98vh', md: '95vh' },
          width: { xs: '98vw', md: '60vw' },
          maxWidth: { xs: '98vw', md: '60vw' },
          borderRadius: { xs: 2, md: 3 },
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          m: { xs: 1, md: 2 },
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        p: 2,
        background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 65%, ${AppStyles.colors.secondary.main})`,
        color: '#fff',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          {currentFile && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" noWrap sx={{ 
                fontWeight: 600,
                color: '#fff',
                textShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}>
                {currentFile.fileName}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {fileContent?.type === 'image' && !isMobile && (
            <>
              <IconButton 
                onClick={handleZoomOut} 
                disabled={zoom <= 0.5}
                sx={{
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                <ZoomOutIcon />
              </IconButton>
              <Typography variant="body2" sx={{ 
                minWidth: 50, 
                textAlign: 'center',
                color: '#fff',
                fontWeight: 600,
                textShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <IconButton 
                onClick={handleZoomIn} 
                disabled={zoom >= 3}
                sx={{
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                <ZoomInIcon />
              </IconButton>
              <Box sx={{ width: 1, height: 24, backgroundColor: 'rgba(255,255,255,0.2)', mx: 1 }} />
            </>
          )}
          
          {files.length > 1 && (
            <>
              <Box sx={{ width: 1, height: 24, backgroundColor: 'rgba(255,255,255,0.2)', mx: 1 }} />
              <IconButton 
                onClick={handlePrevious} 
                disabled={currentIndex === 0}
                sx={{
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                <NavigateBeforeIcon />
              </IconButton>
              <Box sx={{
                px: 1.5,
                py: 0.5,
                backgroundColor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 2,
                minWidth: 60,
                textAlign: 'center'
              }}>
                <Typography variant="caption" sx={{ 
                  color: '#fff',
                  fontWeight: 600,
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }}>
                  {currentIndex + 1} {t('fileViewer.of')} {files.length}
                </Typography>
              </Box>
              <IconButton 
                onClick={handleNext} 
                disabled={currentIndex === files.length - 1}
                sx={{
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                <NavigateNextIcon />
              </IconButton>
            </>
          )}
          
          <Box sx={{ width: 1, height: 24, backgroundColor: 'rgba(255,255,255,0.2)', mx: 1 }} />
          <IconButton 
            onClick={handleDownload}
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              }
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: 0, 
        overflow: 'hidden',
        backgroundColor: '#f8f9fa',
        position: 'relative'
      }}>
        {renderFileContent()}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        backgroundColor: '#fff',
        borderTop: '1px solid rgba(0,0,0,0.05)',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t('fileViewer.file')} {currentIndex + 1} {t('fileViewer.of')} {files.length}
          </Typography>
        </Box>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 65%, ${AppStyles.colors.secondary.main})`,
            color: '#fff',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            '&:hover': {
              background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 65%, ${AppStyles.colors.secondary.main})`,
              boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
            }
          }}
        >
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileViewer;
